import { ONBOARDING_TRANSITION_DURATION_MS } from "@/features/user-profile/constants/onboarding-transition";
import { cn } from "@/utils/cn";
import { triggerHaptic } from "@/utils/haptics";
import { IonBackdrop } from "@ionic/react";
import { useEffect, useState } from "react";

interface OnboardingWarningAlertProps {
  isOpen: boolean;
  message: string;
  onClose: () => void;
}

/**
 * "We do not recommend using NutriCoach…" alert shown on the diagnostic
 * sustainability check when the user taps any option other than "None of these
 * apply". Dismissing (OK or backdrop) keeps the user on the screen — the only
 * way forward is selecting "None of these apply".
 */
export const OnboardingWarningAlert = ({
  isOpen,
  message,
  onClose,
}: OnboardingWarningAlertProps) => {
  const [hasEntered, setHasEntered] = useState(false);

  // Mounted state toggles via `isOpen`, so re-arm the enter transition on every
  // open rather than relying on a fresh mount.
  useEffect(() => {
    if (!isOpen) {
      setHasEntered(false);
      return;
    }
    const raf = requestAnimationFrame(() => setHasEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center px-6">
      <IonBackdrop
        visible
        className={cn("!bg-black transition-opacity", hasEntered ? "!opacity-60" : "!opacity-0")}
        style={{ transitionDuration: `${ONBOARDING_TRANSITION_DURATION_MS}ms` }}
        tappable={true}
        onIonBackdropTap={onClose}
      />
      <div
        className="relative z-10 flex w-full max-w-[330px] flex-col items-center gap-6 rounded-[34px] bg-white p-7 transition-[opacity,transform]"
        style={{
          transitionDuration: `${ONBOARDING_TRANSITION_DURATION_MS}ms`,
          opacity: hasEntered ? 1 : 0,
          transform: hasEntered ? "translateY(0)" : "translateY(24px)",
        }}
      >
        <p className="text-center text-base leading-6 font-bold text-black">{message}</p>

        <button
          type="button"
          onClick={() => {
            triggerHaptic();
            onClose();
          }}
          className="flex h-[50px] w-full items-center justify-center rounded-[40px] bg-[linear-gradient(to_bottom,#513775,#43246e)] text-base font-medium text-white shadow-md"
        >
          OK
        </button>
      </div>
    </div>
  );
};
